import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import { formatCurrency } from "@/lib/utils/currency";
import useEnrollmentInvoice from "@/lib/utils/useEnrollmentInvoice";
import {
    EnrollmentTier,
    EnrollmentType,
    ENROLLMENT_TYPE_OPTIONS,
    getRetailPrice,
    RequestHelpType,
    TIER_OPTIONS,
    tierHasWaterInsights,
} from "@/lib/utils/enrollment";
import { EnrollmentFormField } from "@/types/field";
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalContent,
    ModalFooter,
    Button,
    Divider,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
} from "@heroui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { date, InferType, number, object, string } from "yup";
import DateInput from "../inputs/DateInput";
import SelectButtonInput from "../inputs/SelectButtonInput";
import TextInput from "../inputs/TextInput";
import NumberInputComponent from "../inputs/NumberInput";
import { Status } from "@/types/enums";
import { OrganizationAvailableCredits } from "@/types/organization";

const schema = object().shape({
    start_date: date().required(),
    enrolling_organization: number().nullable(),
    enrollment_tier: string().required(),
    insights_start_date: date().when("enrollment_tier", {
        is: EnrollmentTier.Monitoring.toString(),
        then: (insights_start_date) => insights_start_date.nullable(),
        otherwise: (insights_start_date) => insights_start_date.required(),
    }),
    insights_end_date: date().when("enrollment_tier", {
        is: EnrollmentTier.Monitoring.toString(),
        then: (insights_end_date) => insights_end_date.nullable(),
        otherwise: (insights_end_date) => insights_end_date.required(),
    }),
    water_insights_start_date: date().when("enrollment_tier", {
        is: tierHasWaterInsights,
        then: (water_insights_start_date) =>
            water_insights_start_date.required(),
        otherwise: (water_insights_start_date) =>
            water_insights_start_date.nullable(),
    }),
    water_insights_end_date: date().when("enrollment_tier", {
        is: tierHasWaterInsights,
        then: (water_insights_end_date) => water_insights_end_date.required(),
        otherwise: (water_insights_end_date) =>
            water_insights_end_date.nullable(),
    }),
    acres: number().nullable(),
    retail_price_per_acre: number().nullable(),
    retail_refund_price_per_acre: number().nullable(),
    enrollment_type: string().required(),
});

function EnrollmentForm({
    fieldsToEnroll,
    remainingBalance,
    organizationCreditBalances,
    organizationCreditBalancesLoading,
    organizationCreditBalancesError,
    existingEnrollment,
    onClose,
}: {
    fieldsToEnroll: EnrollmentFormField[];
    remainingBalance: number;
    organizationCreditBalances?: OrganizationAvailableCredits[];
    organizationCreditBalancesLoading?: boolean;
    organizationCreditBalancesError?: boolean;
    existingEnrollment?: EnrollmentFormField;
    onClose: (shouldRefetch: boolean) => void;
}) {
    const { userIsSentinel, isConsultant } = useAuth();
    const { useMutateRequest } = useRequest();
    const {
        irrigationEnabled,
        organizationPreviewEnabled,
        organizationMonitoringEnabled,
    } = useFeatureFlag();
    const f = useForm<InferType<typeof schema>>({
        resolver: yupResolver(schema),
        defaultValues: {
            enrollment_type: EnrollmentType.Commercial,
        },
    });

    const tierOptions = useMemo(() => {
        let options = TIER_OPTIONS;
        if (!organizationMonitoringEnabled) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Monitoring,
            );
        }
        if (!irrigationEnabled) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Irrigation,
            );
        }
        if (!organizationPreviewEnabled && !userIsSentinel) {
            options = options.filter(
                (option) => option.tier !== EnrollmentTier.Preview,
            );
        }
        return options;
    }, [
        organizationMonitoringEnabled,
        irrigationEnabled,
        organizationPreviewEnabled,
        userIsSentinel,
    ]);

    useEffect(() => {
        if (existingEnrollment?.active_enrollment) {
            f.reset({
                start_date: dayjs(
                    existingEnrollment.active_enrollment.start_date,
                ).toDate(),
                enrolling_organization:
                    existingEnrollment.active_enrollment.enrolling_organization,
                enrollment_tier:
                    existingEnrollment.active_enrollment.enrollment_tier.toString(),
                insights_start_date: dayjs(
                    existingEnrollment.active_enrollment.insights_start_date,
                ).toDate(),
                insights_end_date: dayjs(
                    existingEnrollment.active_enrollment.insights_end_date,
                ).toDate(),
                water_insights_start_date: existingEnrollment.active_enrollment
                    .water_insights_start_date
                    ? dayjs(
                        existingEnrollment.active_enrollment
                            .water_insights_start_date,
                    ).toDate()
                    : undefined,
                water_insights_end_date: existingEnrollment.active_enrollment
                    .water_insights_end_date
                    ? dayjs(
                        existingEnrollment.active_enrollment
                            .water_insights_end_date,
                    ).toDate()
                    : undefined,
                acres: existingEnrollment.active_enrollment.acres,
                retail_price_per_acre: Number(
                    existingEnrollment.active_enrollment.retail_price_per_acre,
                ),
                retail_refund_price_per_acre: Number(
                    existingEnrollment.active_enrollment
                        .retail_refund_price_per_acre,
                ),
                enrollment_type:
                    existingEnrollment.active_enrollment.enrollment_type ??
                    EnrollmentType.Commercial,
            });
        }
    }, [existingEnrollment, f]);

    const [enrollmentTier, startDate] = f.watch([
        "enrollment_tier",
        "start_date",
    ]);
    const {
        downloadInvoice,
        isDownloading: isDownloadingInvoice,
        canDownloadInvoice,
    } = useEnrollmentInvoice(
        existingEnrollment ? [existingEnrollment] : fieldsToEnroll,
        !existingEnrollment && enrollmentTier
            ? (Number(enrollmentTier) as EnrollmentTier)
            : undefined,
        !existingEnrollment ? startDate : undefined,
    );

    const sentinelCanCancel = useMemo(() => {
        return (
            existingEnrollment &&
            userIsSentinel &&
            existingEnrollment?.active_enrollment &&
            dayjs(startDate).isAfter(dayjs().add(1, "day"))
        );
    }, [existingEnrollment, userIsSentinel, startDate]);

    const totalAcres = useMemo(() => {
        const fields = existingEnrollment
            ? [existingEnrollment]
            : fieldsToEnroll;

        return fields?.reduce((acc, field) => {
            return acc + field.imagery_boundary_acres;
        }, 0);
    }, [fieldsToEnroll, existingEnrollment]);

    const enrollingOrganizations = useMemo(() => {
        const fields = existingEnrollment
            ? [existingEnrollment]
            : fieldsToEnroll;
        const organizations = fields
            .map((field) => field.grower.service_provider)
            .filter((organization) => !!organization);

        return organizations.filter(
            (organization, index, self) =>
                index ===
                self.findIndex(
                    (candidate) => candidate?.id === organization?.id,
                ),
        );
    }, [fieldsToEnroll, existingEnrollment]);

    const creditBalanceByOrganizationId = useMemo(() => {
        return new Map(
            organizationCreditBalances?.map((balance) => [
                balance.organization_id,
                balance.available_credits,
            ]) ?? [],
        );
    }, [organizationCreditBalances]);

    const retailValue = useMemo(() => {
        if (!enrollmentTier) return undefined;
        const price = getRetailPrice(
            enrollmentTier as unknown as EnrollmentTier,
        );

        return Number(totalAcres) * price;
    }, [enrollmentTier, totalAcres]);

    const enoughCredits = useMemo(() => {
        if (userIsSentinel) return true;
        return Number(remainingBalance) >= Number(retailValue);
    }, [remainingBalance, retailValue, userIsSentinel]);

    const updateMutation = useMutateRequest({
        errorMessage:
            "Failed to update field enrollment, please try again or contact your account manager",
        successMessage: "Field enrollment successfully updated",
    });

    const enrollMutation = useMutateRequest({
        errorMessage:
            "Failed to enroll fields, please try again or contact your account manager",
        successMessage: "Fields successfully enrolled",
    });

    const onSubmit = useCallback(
        (data: InferType<typeof schema>) => {
            const waterInsightsDates = tierHasWaterInsights(
                data.enrollment_tier,
            )
                ? {
                    water_insights_start_date: dayjs(
                        data.water_insights_start_date,
                    ).format("YYYY-MM-DD"),
                    water_insights_end_date: dayjs(
                        data.water_insights_end_date,
                    ).format("YYYY-MM-DD"),
                }
                : {
                    water_insights_start_date: null,
                    water_insights_end_date: null,
                };
            if (existingEnrollment) {
                const updatedEnrollment = {
                    start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
                    insights_start_date: dayjs(data.insights_start_date).format(
                        "YYYY-MM-DD",
                    ),
                    insights_end_date: dayjs(data.insights_end_date).format(
                        "YYYY-MM-DD",
                    ),
                    ...waterInsightsDates,
                    enrollment_tier: data.enrollment_tier,
                    enrolling_organization:
                        existingEnrollment.grower.service_provider?.id,
                    acres: data.acres,
                    retail_price_per_acre: data.retail_price_per_acre,
                    retail_refund_price_per_acre:
                        data.retail_refund_price_per_acre,
                    enrollment_type: data.enrollment_type,
                };

                updateMutation.mutate(
                    {
                        url: `field_enrollments/${existingEnrollment.active_enrollment?.id}/`,
                        method: "patch",
                        data: updatedEnrollment,
                    },
                    {
                        onSuccess: () => {
                            onClose(true);
                        },
                    },
                );
            } else {
                const enrollmentData = fieldsToEnroll?.map((field) => {
                    return {
                        field: field.id,
                        start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
                        insights_start_date: dayjs(
                            data.insights_start_date,
                        ).format("YYYY-MM-DD"),
                        insights_end_date: dayjs(data.insights_end_date).format(
                            "YYYY-MM-DD",
                        ),
                        ...waterInsightsDates,
                        enrollment_tier: data.enrollment_tier,
                        enrolling_organization:
                            field.grower.service_provider?.id,
                        acres: data.acres,
                        retail_price_per_acre: data.retail_price_per_acre,
                        retail_refund_price_per_acre:
                            data.retail_refund_price_per_acre,
                        enrollment_type: data.enrollment_type,
                    };
                });

                enrollMutation.mutate(
                    {
                        url: "field_enrollments/enroll/",
                        method: "post",
                        data: enrollmentData,
                    },
                    {
                        onSuccess: () => {
                            onClose(true);
                        },
                    },
                );
            }
        },
        [
            existingEnrollment,
            fieldsToEnroll,
            enrollMutation,
            updateMutation,
            onClose,
        ],
    );

    const cancelMutation = useMutateRequest({
        url: `field_enrollments/${existingEnrollment?.active_enrollment?.id}/`,
        method: "delete",
        errorMessage: "Failed to cancel enrollment",
        successMessage: "Successfully canceled enrollment",
    });

    const cancelEnrollment = useCallback(() => {
        cancelMutation.mutate(
            {
                data: {
                    status: Status.Inactive,
                },
            },
            {
                onSuccess: () => {
                    onClose(true);
                },
            },
        );
    }, [cancelMutation, onClose]);

    const requestHelpMutation = useMutateRequest({
        url: `field_enrollments/request_help/`,
        method: "post",
        errorMessage: "Failed to request help",
        successMessage: "Your request has been sent to your account manager",
    });

    const requestHelp = useCallback(
        (type: RequestHelpType, fieldId: number) => {
            requestHelpMutation.mutate({
                data: {
                    type,
                    field: fieldId,
                },
            });
        },
        [requestHelpMutation],
    );

    useEffect(() => {
        if (enrollmentTier === EnrollmentTier.Monitoring.toString()) {
            f.setValue("insights_start_date", undefined);
            f.setValue("insights_end_date", undefined);
        }
        if (!enrollmentTier) return;
        if (tierHasWaterInsights(enrollmentTier)) {
            const waterStart = f.getValues("water_insights_start_date");
            const waterEnd = f.getValues("water_insights_end_date");
            const nitrogenStart = f.getValues("insights_start_date");
            const nitrogenEnd = f.getValues("insights_end_date");
            if (!waterStart && nitrogenStart) {
                f.setValue("water_insights_start_date", nitrogenStart);
            }
            if (!waterEnd && nitrogenEnd) {
                f.setValue("water_insights_end_date", nitrogenEnd);
            }
        } else {
            f.setValue("water_insights_start_date", undefined);
            f.setValue("water_insights_end_date", undefined);
        }
    }, [enrollmentTier, f]);

    return (
        <FormProvider {...f}>
            <Modal isOpen onClose={() => onClose(false)} size="xl" scrollBehavior="inside">
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-2 pb-0">
                        <div>
                            {existingEnrollment ? "Updating " : "Enrolling "}
                            {existingEnrollment
                                ? 1
                                : fieldsToEnroll?.length}{" "}
                            Field(s)
                        </div>
                        <div className="text-xs">
                            {fieldsToEnroll?.map((field) => (
                                <div key={field.id}>
                                    {field.name}
                                    {field.grower.name
                                        ? ` - ${field.grower.name}`
                                        : ""}
                                    {field.grower.service_provider?.name
                                        ? ` - ${field.grower.service_provider.name}`
                                        : ""}
                                </div>
                            ))}
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <DateInput
                            name="start_date"
                            label="Enrollment Start Date"
                            labelPlacement="outside"
                            size="sm"
                            isReadOnly={!!existingEnrollment && !userIsSentinel}
                            isDisabled={!!existingEnrollment && !userIsSentinel}
                        />
                        <SelectButtonInput
                            name="enrollment_tier"
                            options={tierOptions.map((tier) => ({
                                description: tier.label,
                                id: tier.tier,
                            }))}
                            label="Enrollment Tier"
                            orientation="horizontal"
                            classNames={{
                                base: "flex-1 w-full",
                                wrapper: "grid grid-cols-3",
                            }}
                            isReadOnly={!!existingEnrollment && !userIsSentinel}
                            isDisabled={!!existingEnrollment && !userIsSentinel}
                        />
                        {userIsSentinel && <SelectButtonInput
                            name="enrollment_type"
                            options={ENROLLMENT_TYPE_OPTIONS.map((option) => ({
                                description: option.description,
                                id: option.id,
                            }))}
                            label="Enrollment Type"
                            orientation="horizontal"
                            classNames={{
                                base: "flex-1 w-full",
                                wrapper: "grid grid-cols-4",
                            }}
                            isReadOnly={!!existingEnrollment && !userIsSentinel}
                            isDisabled={!!existingEnrollment && !userIsSentinel}
                            defaultValue={EnrollmentType.Commercial}
                        />}
                        {startDate && (
                            <p className="text-sm text-gray-500">
                                Imagery will end on{" "}
                                {dayjs(startDate)
                                    .add(1, "year")
                                    .format("MM/DD/YYYY")}
                            </p>
                        )}
                        {enrollmentTier ===
                            EnrollmentTier.Monitoring.toString() && (
                                <p className="text-sm text-red-500">
                                    Insights are not available for monitoring
                                    enrollments
                                </p>
                            )}
                        <div className="flex flex-row gap-2">
                            <DateInput
                                labelPlacement="outside"
                                name="insights_start_date"
                                label="Nitrogen Insights Start Date"
                                size="sm"
                                isDisabled={
                                    enrollmentTier ===
                                    EnrollmentTier.Monitoring.toString()
                                }
                            />
                            <DateInput
                                labelPlacement="outside"
                                name="insights_end_date"
                                label="Nitrogen Insights End Date"
                                size="sm"
                                isDisabled={
                                    enrollmentTier ===
                                    EnrollmentTier.Monitoring.toString()
                                }
                            />
                        </div>
                        {irrigationEnabled && tierHasWaterInsights(enrollmentTier) && (
                            <div className="flex flex-row gap-2">
                                <DateInput
                                    labelPlacement="outside"
                                    name="water_insights_start_date"
                                    label="Water Insights Start Date"
                                    size="sm"
                                    isDisabled={
                                        !tierHasWaterInsights(enrollmentTier)
                                    }
                                />
                                <DateInput
                                    labelPlacement="outside"
                                    name="water_insights_end_date"
                                    label="Water Insights End Date"
                                    size="sm"
                                    isDisabled={
                                        !tierHasWaterInsights(enrollmentTier)
                                    }
                                />
                            </div>
                        )}
                        {irrigationEnabled &&
                            !!enrollmentTier &&
                            !tierHasWaterInsights(enrollmentTier) && (
                                <p className="text-sm text-red-500">
                                    Water insights are only available for
                                    Irrigation and Complete enrollments
                                </p>
                            )}
                        <p className="text-sm text-red-500">
                            After enrolling, you will not be able to change the
                            tier, enrollment start date, or boundary without
                            contacting your account manager
                        </p>

                        {!!existingEnrollment && userIsSentinel && (
                            <>
                                <TextInput
                                    name="acres"
                                    label="Acres"
                                    type="number"
                                    labelPlacement="outside"
                                    size="sm"
                                    placeholder=" "
                                />
                                <NumberInputComponent
                                    name="retail_price_per_acre"
                                    label="Retail Price Per Acre"
                                    type="number"
                                    labelPlacement="outside"
                                    size="sm"
                                    placeholder=" "
                                    startContent="$"
                                />
                                <TextInput
                                    name="retail_refund_price_per_acre"
                                    label="Retail Refund Price Per Acre"
                                    type="number"
                                    labelPlacement="outside"
                                    placeholder=" "
                                    size="sm"
                                    startContent="$"
                                />
                            </>
                        )}
                        {!!existingEnrollment && isConsultant && (
                            <>
                                <Divider />
                                <div className="text-red-500 text-sm">
                                    Use the buttons below request an enrollment
                                    change, a refund, or contact your account
                                    manager directly
                                </div>
                                <div className="flex flex-row gap-2">
                                    <Button
                                        fullWidth
                                        color="primary"
                                        isLoading={
                                            requestHelpMutation.isPending
                                        }
                                        isDisabled={!existingEnrollment?.id}
                                        onPress={() => {
                                            requestHelp(
                                                RequestHelpType.request_enrollment_update,
                                                existingEnrollment?.id,
                                            );
                                        }}
                                    >
                                        Request Enrollment Change
                                    </Button>
                                    <Button
                                        fullWidth
                                        color="primary"
                                        isLoading={
                                            requestHelpMutation.isPending
                                        }
                                        isDisabled={!existingEnrollment?.id}
                                        onPress={() => {
                                            requestHelp(
                                                RequestHelpType.request_refund,
                                                existingEnrollment?.id,
                                            );
                                        }}
                                    >
                                        Request Refund
                                    </Button>
                                </div>
                            </>
                        )}
                        <Divider />
                    </ModalBody>
                    <ModalFooter className="flex flex-col gap-2">
                        <div className="flex flex-row justify-between">
                            <div>Total Acres:</div>
                            <div className="font-semibold">{totalAcres}</div>
                        </div>
                        <div className="flex flex-row justify-between">
                            <div>Retail value:</div>
                            <div className="font-semibold">
                                {retailValue
                                    ? formatCurrency(retailValue)
                                    : "--"}
                            </div>
                        </div>
                        {userIsSentinel &&
                            enrollingOrganizations.map((organization) => {
                                const balance =
                                    creditBalanceByOrganizationId.get(
                                        organization.id,
                                    );
                                return (
                                    <div
                                        key={organization.id}
                                        className="flex flex-row justify-between"
                                    >
                                        <div>
                                            {organization.name
                                                ? `${organization.name} `
                                                : ""}
                                            CSP Balance:
                                        </div>
                                        <div className="font-semibold">
                                            {organizationCreditBalancesLoading
                                                ? "Loading..."
                                                : organizationCreditBalancesError
                                                    ? "--"
                                                    : (formatCurrency(balance) ??
                                                        "--")}
                                        </div>
                                    </div>
                                );
                            })}
                        {userIsSentinel && organizationCreditBalancesError && (
                            <p className="text-red-500 text-sm">
                                Unable to load CSP balance
                            </p>
                        )}

                        {!enoughCredits && (
                            <p className="text-red-500 text-sm">
                                You do not have enough credits to enroll these
                                field(s), please contact your account manager
                            </p>
                        )}
                        {userIsSentinel && (
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="flat"
                                        fullWidth
                                        isLoading={isDownloadingInvoice}
                                        isDisabled={
                                            !canDownloadInvoice ||
                                            isDownloadingInvoice ||
                                            enrollMutation.isPending ||
                                            updateMutation.isPending ||
                                            (!existingEnrollment && !enrollmentTier)
                                        }
                                    >
                                        Download Invoice
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu>
                                    <DropdownItem
                                        key="without-pricing"
                                        onPress={() => downloadInvoice()}
                                    >
                                        Without pricing
                                    </DropdownItem>
                                    <DropdownItem
                                        key="with-pricing"
                                        onPress={() => downloadInvoice(true)}
                                    >
                                        With pricing
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        )}
                        <div className="flex flex-row gap-2">
                            <Button
                                size="sm"
                                variant="flat"
                                onPress={() => onClose(false)}
                                fullWidth
                                isDisabled={
                                    enrollMutation.isPending ||
                                    updateMutation.isPending
                                }
                                isLoading={
                                    enrollMutation.isPending ||
                                    updateMutation.isPending
                                }
                            >
                                Cancel
                            </Button>
                            {sentinelCanCancel && (
                                <Button
                                    size="sm"
                                    color="danger"
                                    fullWidth
                                    onPress={cancelEnrollment}
                                    isLoading={updateMutation.isPending}
                                >
                                    Cancel Enrollment
                                </Button>
                            )}
                            <Button
                                size="sm"
                                color="primary"
                                onPress={() => {
                                    f.trigger();
                                    f.handleSubmit(onSubmit)();
                                }}
                                fullWidth
                                isDisabled={
                                    enrollMutation.isPending ||
                                    updateMutation.isPending ||
                                    !enoughCredits
                                }
                                isLoading={
                                    enrollMutation.isPending ||
                                    updateMutation.isPending
                                }
                            >
                                {existingEnrollment ? "Update" : "Enroll"}
                            </Button>
                        </div>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </FormProvider>
    );
}

export default EnrollmentForm;
